/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useFormatter, useLocale, useTranslations } from "@i18n/react";
import type { JSX } from "react";
import { forwardRef, useCallback, useEffect, useState } from "react";

import { spacing } from "../../../../../global/emotion";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import { Text } from "../../../_base/_core/Text/Text.emotion";
import type { PrayerPopupProps } from "../PrayerPopup/PrayerPopup.emotion";
import { PrayerPopup } from "../PrayerPopup/PrayerPopup.emotion";

/**
 * MAX_INCREMENT: Maximum increase
 * MIN_INCREMENT: Minimum increase
 * MAX_TIMER_DELAY: Maximum timer delay (ms)
 * MIN_TIMER_DELAY: Minimum timer delay (ms)
 */
const MAX_INCREMENT = 67;
const MIN_INCREMENT = 44;
const MAX_TIMER_DELAY = 6000;
const MIN_TIMER_DELAY = 4000;

type RandomPrayerJson = Array<{
  options: Array<{ id: number; title: string }>;
  type: "random" | "week" | "constant";
  image: string;
}>;

const containerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
};
const determinedElementStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.xl,
  width: spacing.full,
};
const emptyPopupStyle: Interpolation = {
  height: "60px",
};

const getRandomTimer = () => {
  return Math.floor(Math.random() * MAX_TIMER_DELAY) + MIN_TIMER_DELAY;
};

const hallowElement: HallowElement = "div";

export type PrayerCounterProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & { initialCount: number };

const getRandomPrayer = (prayers: RandomPrayerJson) => {
  if (!prayers?.length) return null;
  const collection = prayers[Math.floor(Math.random() * prayers.length)];
  if (!collection) return null;
  if (collection.type === "constant") {
    return { title: collection.options[0].title, image: collection.image };
  } else if (collection.type === "week") {
    const day = new Date().getDay();
    return { title: collection.options[day].title, image: collection.image };
  } else {
    const idx = Math.floor(Math.random() * collection.options.length);
    return { title: collection.options[idx].title, image: collection.image };
  }
};

export const PrayerCounter = forwardRef<any, PrayerCounterProps>(
  ({ asChild = false, children, initialCount, ...props }, ref) => {
    const t = useTranslations();
    const locale = useLocale();
    const [prayers, setPrayers] = useState<RandomPrayerJson>([]);
    const [popup, setPopup] = useState<PrayerPopupProps>(null);

    const [count, setCount] = useState<number>(initialCount);
    const format = useFormatter();

    const doPopup = useCallback(() => {
      setPopup(getRandomPrayer(prayers));
    }, [prayers]);

    const updateCount = useCallback(() => {
      const increment =
        Math.floor(Math.random() * MAX_INCREMENT) + MIN_INCREMENT;
      setCount((prevCount) => prevCount + increment);
      doPopup();
    }, [doPopup]);

    useEffect(() => {
      const getPrayers = async (locale: string) => {
        setPrayers(await import(`./random-prayers/${locale}.json`));
      };

      getPrayers(locale);
    }, [locale]);

    useEffect(() => {
      // do the first popup immediately
      doPopup();
    }, [doPopup]);

    useEffect(() => {
      const timer = setTimeout(updateCount, getRandomTimer());
      return () => clearTimeout(timer);
    }, [count, updateCount]);

    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const DeterminedChildren = determineChildrenInject({
      afterChildrenInject: [
        <div key="container" css={containerStyle}>
          <Text type="display" size="s">
            {format.number(count)}
          </Text>
          <Text type="body" size="m">
            {t("prayers_prayed_with_hallow")}
          </Text>
        </div>,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement ref={ref} css={determinedElementStyle} {...props}>
        {popup ? (
          <PrayerPopup key={JSON.stringify(popup)} {...popup} />
        ) : (
          <div css={emptyPopupStyle} />
        )}
        {DeterminedChildren}
      </DeterminedElement>
    );
  },
);

PrayerCounter.displayName = "PrayerCounter";
